import { KeyboardEvent, ReactNode, useId, useRef } from 'react';
import './styles/SelectionTileGroup.scss';

import { Stack, Typography } from '@mui/material';

import { classNames, componentClassName } from '@dw/utils';

import { Card } from './card';
import { HStack } from './HStack';

const cn = componentClassName('selection-tile-group');

export interface ISelectionTileOption<TValue extends string> {
  value: TValue;
  label: string;
  /** A picture of the choice. This is what earns a tile its size over a radio button. */
  icon: ReactNode;
  /** One short line under the label — what this option means, not what it is called. */
  caption?: string;
  disabled?: boolean;
}

export interface ISelectionTileGroupProps<TValue extends string> {
  options: ISelectionTileOption<TValue>[];
  value: TValue;
  onChange: (value: TValue) => void;
  /** Renders a label above the tiles and names the group for screen readers. */
  label?: string;
  /** Id of a heading the caller already renders. Use instead of `label`, never as well as. */
  labelledBy?: string;
  disabled?: boolean;
  /**
   * The icon beside the words rather than above them — for a choice made in passing inside a form,
   * where a full tile's picture block would outweigh the field the choice belongs to.
   */
  dense?: boolean;
  className?: string;
}

/** The next option that can actually be picked, wrapping past either end and skipping disabled ones. */
const nextEnabledIndex = <TValue extends string>(
  options: ISelectionTileOption<TValue>[],
  fromIndex: number,
  step: number
): number => {
  for (let offset = 1; offset <= options.length; offset++) {
    const index = (((fromIndex + step * offset) % options.length) + options.length) % options.length;
    if (!options[index].disabled) {
      return index;
    }
  }
  return fromIndex;
};

/**
 * A single choice made by clicking a picture of it.
 *
 * Where a `FormSelectInput` hides its options behind a click and a radio reduces each one to a dot,
 * a tile shows what it means — a shape, a glyph, an icon — at a size that can be hit without aiming.
 * Use it for short, fixed sets (two to four) whose options are easier to recognise than to read.
 *
 * It is a real radio group: one tab stop for the whole set, arrow keys to move between the tiles
 * (selecting as they go, as radios do), Home/End for the ends, and Space/Enter to confirm.
 */
export function SelectionTileGroup<TValue extends string>(props: ISelectionTileGroupProps<TValue>) {

  const { options, value, onChange, label, labelledBy, disabled, dense, className } = props;

  const groupRef = useRef<HTMLDivElement>(null);
  const generatedLabelId = useId();
  const labelId = label ? generatedLabelId : labelledBy;

  const selectedIndex = options.findIndex(x => x.value === value);
  /*
    The roving tab stop: the whole group is ONE stop in the tab order, on the selected tile. With
    nothing selected it falls to the first pickable tile, so the group is never skipped entirely.
  */
  const tabStopIndex = selectedIndex >= 0 ? selectedIndex : options.findIndex(x => !x.disabled);

  /*
    Focus is moved by asking the group's own DOM for the tile rather than by keeping a ref per tile:
    `Card` is a plain function component and has no ref to give.
  */
  const selectAndFocus = (index: number) => {
    onChange(options[index].value);
    groupRef.current?.querySelector<HTMLElement>(`[data-tile-index='${index}']`)?.focus();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>, index: number) => {
    if (disabled) {
      return;
    }

    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        event.preventDefault();
        selectAndFocus(nextEnabledIndex(options, index, 1));
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        event.preventDefault();
        selectAndFocus(nextEnabledIndex(options, index, -1));
        break;
      case 'Home':
        event.preventDefault();
        selectAndFocus(nextEnabledIndex(options, options.length - 1, 1));
        break;
      case 'End':
        event.preventDefault();
        selectAndFocus(nextEnabledIndex(options, 0, -1));
        break;
      case ' ':
      case 'Enter':
        event.preventDefault();
        if (!options[index].disabled) {
          onChange(options[index].value);
        }
        break;
    }
  };

  return (
    <Stack ref={groupRef} rowGap={1} className={classNames(cn(), { [cn('dense')]: !!dense }, className)}>
      {!!label && (
        <Typography id={labelId} variant='overline' color='textDisabled' fontWeight={600} lineHeight={1.4}>
          {label}
        </Typography>
      )}

      {/*
        Never stacks: the tiles exist to be compared with each other, and a column of them is just a
        list of radio buttons wearing a costume. Two to four short options stay comfortable at phone
        width — which is the size of set this control is for.
      */}
      <HStack breakpoint={false} columnGap={1} alignItems='stretch' role='radiogroup' aria-labelledby={labelId}>
        {options.map((option, index) => {
          const isSelected = option.value === value;
          const isDisabled = !!(disabled || option.disabled);

          return (
            <Card
              key={option.value}
              className={cn('tile')}
              // Equal widths, not content widths: the tiles exist to be compared, and a caption a few
              // words longer must not make its tile read as the "bigger" option. The glyph is a
              // fixed-size Box, so it simply centres in the shared width.
              sx={{ flex: 1 }}
              data-tile-index={index}
              role='radio'
              aria-checked={isSelected}
              aria-disabled={isDisabled}
              tabIndex={index === tabStopIndex ? 0 : -1}
              onClick={() => { if (!isDisabled) onChange(option.value); }}
              onKeyDown={(event) => handleKeyDown(event, index)}
            >
              <Stack
                direction={dense ? 'row' : 'column'}
                rowGap={0.5}
                columnGap={1.5}
                alignItems='center'
                textAlign={dense ? 'left' : 'center'}
              >
                <Stack className={cn('icon')} alignItems='center' justifyContent='center'>
                  {option.icon}
                </Stack>

                <Stack rowGap={0.5}>
                  <Typography variant='body2' fontWeight={600} lineHeight={1.3}>{option.label}</Typography>

                  {!!option.caption && (
                    <Typography variant='caption' lineHeight={1.3}>{option.caption}</Typography>
                  )}
                </Stack>
              </Stack>
            </Card>
          );
        })}
      </HStack>
    </Stack>
  );
}
